// frontend/src/pages/DataWorkspacePage/VisualQueryBuilder.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../api/index.js';
import { useNotification } from '../../context/NotificationContext.jsx';
import { DynamicIcon } from '../../components/icons.jsx';

const VisualQueryBuilder = ({ token, connectionId, tableName, schemaName }) => {
    const { addNotification } = useNotification();
    const [workspace, setWorkspace] = useState({
        source: tableName || "",
        schema: schemaName,
        columns: ["*"],
        joins: [],
        filters: [],
        group_by: [],
        sort_col: "",
        sort_dir: "ASC",
        limit: 100
    });

    const [previewData, setPreviewData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [rawSql, setRawSql] = useState("");
    const [schemaData, setSchemaData] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isColDragOver, setIsColDragOver] = useState(false);

    // Fetch detailed schema (tables and columns)
    useEffect(() => {
        const fetchSchema = async () => {
            try {
                const data = await apiFetch(`/datasources/${connectionId}/structure`, token);
                if (data && data.tables) {
                    setSchemaData(data.tables);
                }
            } catch (err) {
                console.error("Failed to fetch schema", err);
            }
        };
        if (connectionId) fetchSchema();
    }, [connectionId, token]);

    // Effect to update workspace state when source table changes externally
    useEffect(() => {
        if (tableName) {
            setWorkspace(prev => ({
                ...prev,
                source: tableName,
                schema: schemaName,
                columns: ["*"],
                joins: [],
                filters: []
            }));
            setPreviewData(null);
            setRawSql("");
        }
    }, [tableName, schemaName]);

    // --- Graph Canvas Drop Handlers ---
    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = () => {
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;
        
        try {
            const tableData = JSON.parse(dataStr);
            if (tableData.type !== 'table') return;

            // If empty canvas, set as source
            if (!workspace.source) {
                setWorkspace(prev => ({...prev, source: tableData.tableName, columns: ["*"]}));
                return;
            }

            if (workspace.source === tableData.tableName) return; // dropped same thing
            if (workspace.joins.some(j => j.table === tableData.tableName)) {
                addNotification(`${tableData.tableName} already joined.`, 'warning');
                return;
            }

            // Phase 3 Auto-Join Logic (Deep Semantic Mapping)
            let source_col = "";
            let join_col = "";
            let join_type = "LEFT";

            const sourceSchema = schemaData.find(t => t.name === workspace.source);

            // 1. Target table fk maps to Source table pk
            if (tableData.foreignKeys) {
                const fkToSource = tableData.foreignKeys.find(fk => fk.referred_table === workspace.source);
                if (fkToSource && fkToSource.constrained_columns.length > 0 && fkToSource.referred_columns.length > 0) {
                    join_col = fkToSource.constrained_columns[0];
                    source_col = fkToSource.referred_columns[0];
                }
            }

            // 2. Source table fk maps to Target table pk
            if (!source_col && sourceSchema && sourceSchema.foreign_keys) {
                const fkFromSource = sourceSchema.foreign_keys.find(fk => fk.referred_table === tableData.tableName);
                if (fkFromSource && fkFromSource.constrained_columns.length > 0 && fkFromSource.referred_columns.length > 0) {
                    source_col = fkFromSource.constrained_columns[0];
                    join_col = fkFromSource.referred_columns[0];
                }
            }

            // 3. Fallback heuristic: exact match of identity columns
            if (!source_col) {
                const targetCols = tableData.columns || [];
                const srcCols = sourceSchema ? sourceSchema.columns : [];
                for (const scol of srcCols) {
                    const sname = scol.name.toLowerCase();
                    if (sname.endsWith("key") || sname.endsWith("id") || sname === "id") {
                        const exactMatch = targetCols.find(tc => tc.name === scol.name);
                        if (exactMatch) {
                            source_col = scol.name;
                            join_col = exactMatch.name;
                            break;
                        }
                    }
                }
            }

            setWorkspace(prev => ({
                ...prev,
                joins: [
                    ...prev.joins,
                    {
                        table: tableData.tableName,
                        type: join_type,
                        source_col: source_col,
                        join_col: join_col,
                        is_auto: !!source_col
                    }
                ]
            }));

            if (source_col) {
                addNotification(`Auto-joined ${tableData.tableName} via ${source_col} = ${join_col}`, 'success');
            } else {
                addNotification(`Added ${tableData.tableName}. Please link explicitly.`, 'warning');
            }
        } catch(err) {
            console.error("Drop err", err);
        }
    };

    // --- Column Drop Handlers ---
    const handleColDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsColDragOver(true);
    };

    const handleColDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsColDragOver(false);
    };

    const handleColDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsColDragOver(false);

        const dataStr = e.dataTransfer.getData('application/json');
        if (!dataStr) return;
        
        try {
            const payload = JSON.parse(dataStr);
            if (payload.type !== 'column') return;

            const qualifiedCol = `${payload.table}.${payload.column}`;

            setWorkspace(prev => {
                let nextCols = [...prev.columns];
                if (nextCols.length === 1 && nextCols[0] === "*") {
                    nextCols = [qualifiedCol];
                } else if (!nextCols.includes(qualifiedCol)) {
                    nextCols.push(qualifiedCol);
                }
                
                // Check if we need to auto-join the table that owns this column
                if (prev.source && prev.source !== payload.table && !prev.joins.some(j => j.table === payload.table)) {
                    // Try to auto-resolve
                    const tableSchema = schemaData.find(t => t.name === payload.table);
                    const sourceSchema = schemaData.find(t => t.name === prev.source);
                    
                    let source_col = "";
                    let join_col = "";
                    
                    // target fk to source pk
                    if (tableSchema && tableSchema.foreign_keys) {
                        const fkToSource = tableSchema.foreign_keys.find(fk => fk.referred_table === prev.source);
                        if (fkToSource && fkToSource.constrained_columns.length > 0) {
                            join_col = fkToSource.constrained_columns[0];
                            source_col = fkToSource.referred_columns[0];
                        }
                    }
                    
                    // source fk to target pk
                    if (!source_col && sourceSchema && sourceSchema.foreign_keys) {
                        const fkFromSource = sourceSchema.foreign_keys.find(fk => fk.referred_table === payload.table);
                        if (fkFromSource && fkFromSource.constrained_columns.length > 0) {
                            source_col = fkFromSource.constrained_columns[0];
                            join_col = fkFromSource.referred_columns[0];
                        }
                    }
                    
                    // Fallback heuristics
                    if (!source_col && sourceSchema && tableSchema) {
                        for (const scol of sourceSchema.columns) {
                            const sname = scol.name.toLowerCase();
                            if (sname.endsWith("key") || sname.endsWith("id") || sname === "id") {
                                const exactMatch = tableSchema.columns.find(tc => tc.name === scol.name);
                                if (exactMatch) {
                                    source_col = scol.name;
                                    join_col = exactMatch.name;
                                    break;
                                }
                            }
                        }
                    }

                    const nextJoins = [
                        ...prev.joins,
                        {
                            table: payload.table,
                            type: "LEFT",
                            source_col: source_col,
                            join_col: join_col,
                            is_auto: !!source_col
                        }
                    ];
                    
                    if (source_col) {
                        addNotification(`Auto-joined ${payload.table} via ${source_col} = ${join_col} because you dropped its column!`, 'success');
                    } else {
                        addNotification(`Added ${payload.table} for column. Please link explicitly.`, 'warning');
                    }
                    
                    return { ...prev, columns: nextCols, joins: nextJoins };
                }
                
                return { ...prev, columns: nextCols };
            });
        } catch(err) {
            console.error(err);
        }
    };
    
    const removeColumn = (idx) => {
        setWorkspace(prev => {
            const nextCols = [...prev.columns];
            nextCols.splice(idx, 1);
            if (nextCols.length === 0) nextCols.push("*");
            return { ...prev, columns: nextCols };
        });
    };

    // --- Join Handlers ---
    const addJoin = () => {
        setWorkspace(prev => ({
            ...prev,
            joins: [...(prev.joins || []), { table: "", type: "LEFT", source_col: "", join_col: "" }]
        }));
    };

    const updateJoin = (index, field, value) => {
        const nextJoins = [...(workspace.joins || [])];
        nextJoins[index][field] = value;
        setWorkspace(prev => ({ ...prev, joins: nextJoins }));
    };

    const removeJoin = (index) => {
        const nextJoins = [...(workspace.joins || [])];
        nextJoins.splice(index, 1);
        setWorkspace(prev => ({ ...prev, joins: nextJoins }));
    };

    // --- Filter Handlers ---
    const addFilter = () => {
        setWorkspace(prev => ({
            ...prev,
            filters: [...prev.filters, { column: "", operator: "=", value: "" }]
        }));
    };

    const updateFilter = (index, field, value) => {
        const nextFilters = [...workspace.filters];
        nextFilters[index][field] = value;
        setWorkspace(prev => ({ ...prev, filters: nextFilters }));
    };

    const removeFilter = (index) => {
        const nextFilters = [...workspace.filters];
        nextFilters.splice(index, 1);
        setWorkspace(prev => ({ ...prev, filters: nextFilters }));
    };

    // --- Schema Helpers ---
    const sourceTableColumns = useMemo(() => {
        const tbl = schemaData.find(t => t.name === workspace.source);
        return tbl ? tbl.columns : [];
    }, [schemaData, workspace.source]);

    // --- Execution ---
    const executeQuery = async () => {
        setIsLoading(true);
        try {
            const filters = workspace.filters.filter(f => f.column && f.value).map(f => ({
                column: f.column,
                op: f.operator,
                value: f.value
            }));

            const activeJoins = (workspace.joins || []).filter(j => j.table && j.source_col && j.join_col);

            const result = await apiFetch(
                `/data-workspace/workspaces/execute/ad-hoc`,
                token,
                {
                    method: 'POST',
                    body: JSON.stringify({
                        connection_id: connectionId,
                        workspace_json: { ...workspace, filters, joins: activeJoins },
                        page: 1,
                        page_size: workspace.limit
                    }),
                }
            );
            
            setPreviewData(result);
            setRawSql(`/* Joined Query transpiled successfully */\n// (Preview fetched from source securely)`);
            addNotification('Query Executed Successfully', 'success');
        } catch (err) {
            addNotification(`Query failed: ${err.message}`, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 overflow-hidden p-4 space-y-4">
            
            {/* Control Panel / Semantic Canvas */}
            <div 
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`flex-1 min-h-0 bg-white rounded-xl border-2 border-dashed ${isDragOver ? 'border-indigo-400 bg-indigo-50/30' : 'border-slate-200'} p-5 flex flex-col space-y-5 overflow-y-auto shadow-sm relative transition-colors`}
            >
                {/* Drag hint overlay */}
                {(!workspace.source && !isDragOver) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="flex flex-col items-center gap-2 text-slate-300">
                            <DynamicIcon name="BoxSelect" className="w-12 h-12" />
                            <p className="text-sm font-semibold">Drop tables here to build visual joins</p>
                        </div>
                    </div>
                )}

                <div className="flex items-center justify-between border-b border-slate-200 pb-3 relative z-10">
                    <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                        <DynamicIcon name="Network" className="w-5 h-5 text-indigo-500" />
                        Semantic Auto-Join Canvas
                    </h2>
                    <button 
                        onClick={executeQuery}
                        disabled={isLoading || !workspace.source}
                        className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-md text-sm font-semibold text-white shadow-md flex items-center gap-2 transition disabled:opacity-50"
                    >
                        {isLoading ? <DynamicIcon name="Loader2" className="w-4 h-4 animate-spin" /> : <DynamicIcon name="Play" className="w-4 h-4" />}
                        Run Query
                    </button>
                </div>

                {workspace.source && (
                    <div className="grid grid-cols-2 gap-8 relative z-10">
                        {/* Source Node */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Root Table Node</h3>
                            <div className="bg-gradient-to-r from-blue-50 to-white border border-blue-200 rounded-lg p-3 flex items-center gap-3 shadow-sm hover:shadow-md transition">
                                <div className="bg-blue-100 p-1.5 rounded-md border border-blue-200">
                                    <DynamicIcon name="Table" className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <div className="text-sm font-semibold text-blue-900">{workspace.source}</div>
                                    <div className="text-xs text-blue-600/70">{workspace.schema || 'public schema'}</div>
                                </div>
                            </div>
                        </div>

                        {/* Select Columns Node */}
                        <div className="space-y-3 col-span-2 pt-2 border-t border-slate-100 mt-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                    <DynamicIcon name="Layout" className="w-4 h-4 text-purple-500" /> Active Columns (SELECT)
                                </h3>
                                {workspace.columns.length > 0 && workspace.columns[0] !== "*" && (
                                     <button onClick={() => setWorkspace(prev => ({...prev, columns: ["*"]}))} className="text-[10px] text-slate-400 hover:text-slate-600 bg-slate-100 px-2 py-0.5 rounded">Reset to All</button>
                                )}
                            </div>
                            
                            <div 
                                onDragOver={handleColDragOver}
                                onDragLeave={handleColDragLeave}
                                onDrop={handleColDrop}
                                className={`bg-white border-2 border-dashed ${isColDragOver ? 'border-purple-400 bg-purple-50/50' : 'border-slate-200'} rounded-lg min-h-[60px] p-3 flex flex-wrap gap-2 transition-colors relative shadow-sm`}
                            >
                                {workspace.columns.length === 1 && workspace.columns[0] === "*" ? (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 italic text-xs py-2 pointer-events-none">
                                        <span className="font-semibold">SELECT * (All Columns)</span>
                                        <span className="text-[10px] mt-1 text-center">Drop columns here to pick specific fields<br/>(e.g., DimCareCenter.CareCenterRegion)</span>
                                    </div>
                                ) : (
                                    workspace.columns.map((colStr, idx) => (
                                        <div key={`${colStr}-${idx}`} className="flex items-center gap-1.5 bg-purple-50 border border-purple-200 text-purple-800 px-2.5 py-1 rounded-md text-xs font-mono shadow-sm">
                                            {colStr}
                                            <button onClick={() => removeColumn(idx)} className="ml-1 text-purple-400 hover:text-red-500 transition-colors">
                                                <DynamicIcon name="X" className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* Limit Config */}
                        <div className="space-y-3">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Parameters</h3>
                            <div className="flex items-center gap-3">
                                <span className="text-sm text-slate-600 font-medium">Limit Records:</span>
                                <input 
                                    type="number"
                                    placeholder="Limit"
                                    value={workspace.limit}
                                    onChange={e => setWorkspace(prev => ({ ...prev, limit: Number(e.target.value) }))}
                                    className="bg-white border border-slate-200 rounded-md text-sm px-3 py-1.5 text-slate-800 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 shadow-sm w-24 font-mono"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Graph Edges / Joins */}
                {workspace.source && (
                    <div className="space-y-3 relative z-10">
                        <div className="flex items-center justify-between mt-2">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                                <DynamicIcon name="GitMerge" className="w-3.5 h-3.5 text-orange-500" /> Associated Edges (Joins)
                            </h3>
                            <button onClick={addJoin} className="text-xs text-orange-500 hover:text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded">
                                + Manual Link
                            </button>
                        </div>

                        <div className="space-y-3">
                            {workspace.joins.map((join, i) => {
                                const targetTableSchema = schemaData.find(t => t.name === join.table);
                                const targetColumns = targetTableSchema ? targetTableSchema.columns : [];

                                return (
                                    <div key={i} className="flex flex-col gap-3 bg-gradient-to-r from-orange-50 to-white p-3 rounded-lg border border-orange-200 shadow-sm">
                                        <div className="flex items-center gap-3">
                                            {join.is_auto ? (
                                                 <div className="px-2 py-1 bg-green-100 border border-green-300 rounded flex gap-1 items-center text-[10px] font-bold text-green-700 shadow-sm">
                                                    <DynamicIcon name="Link" className="w-3 h-3" /> AUTO-RESOLVED
                                                 </div>
                                            ) : (
                                                <div className="px-2 py-1 bg-orange-100 border border-orange-200 rounded text-[10px] font-bold text-orange-700 shadow-sm">
                                                    CUSTOM JOIN
                                                </div>
                                            )}
                                            
                                            <select
                                                value={join.type}
                                                onChange={e => updateJoin(i, 'type', e.target.value)}
                                                className="bg-white border border-slate-200 rounded text-xs px-2 py-1 text-slate-800 outline-none w-24 shadow-sm font-semibold"
                                            >
                                                <option value="LEFT">LEFT</option>
                                                <option value="INNER">INNER</option>
                                                <option value="RIGHT">RIGHT</option>
                                            </select>
                                            
                                            <div className="flex-1 px-3 py-1 bg-white border border-slate-200 rounded text-sm text-slate-800 font-semibold shadow-sm">
                                                {join.table || "Select Target Table"}
                                            </div>

                                            <button onClick={() => removeJoin(i)} className="p-1 text-red-500 hover:bg-red-100 rounded-lg ml-auto transition">
                                                <DynamicIcon name="X" className="w-4 h-4" />
                                            </button>
                                        </div>

                                        <div className="flex items-center gap-3 pl-3 sm:pl-32">
                                            <select
                                                value={join.source_col}
                                                onChange={e => updateJoin(i, 'source_col', e.target.value)}
                                                className="bg-white border border-slate-300 rounded text-xs px-2 py-1 text-slate-700 flex-1 max-w-[200px]"
                                            >
                                                <option value="">{workspace.source} column...</option>
                                                {sourceTableColumns.map(c => (
                                                    <option key={c.name} value={c.name}>{c.name}</option>
                                                ))}
                                            </select>

                                            <DynamicIcon name="ArrowRightLeft" className="w-4 h-4 text-slate-400 shrink-0" />

                                            <select
                                                value={join.join_col}
                                                onChange={e => updateJoin(i, 'join_col', e.target.value)}
                                                className={`bg-white border text-xs px-2 py-1 flex-1 max-w-[200px] rounded ${join.join_col ? 'border-emerald-300 text-emerald-700 font-medium' : 'border-slate-300 text-slate-700'}`}
                                                disabled={!join.table}
                                            >
                                                <option value="">Target column...</option>
                                                {targetColumns.map(c => (
                                                    <option key={c.name} value={c.name}>{c.name}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Filters Node */}
                {workspace.source && (
                <div className="space-y-3 relative z-10 pt-4 border-t border-slate-100">
                    <div className="flex items-center justify-between">
                        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                            <DynamicIcon name="Filter" className="w-3.5 h-3.5 text-slate-400" /> Data Filtering Node
                        </h3>
                        <button onClick={addFilter} className="text-xs text-indigo-500 hover:text-indigo-600 font-medium bg-indigo-50 hover:bg-indigo-100 px-3 py-1 rounded transition">
                            + Add Filter Expression
                        </button>
                    </div>

                    <div className="space-y-2">
                        {workspace.filters.map((f, i) => (
                            <div key={i} className="flex items-center gap-3 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                                <div className="px-2 py-1 bg-white rounded border border-slate-200 text-[10px] font-mono font-bold text-slate-600 shadow-sm">WHERE</div>
                                
                                <input
                                    placeholder="Column Name (e.g. status)"
                                    value={f.column}
                                    onChange={e => updateFilter(i, 'column', e.target.value)}
                                    className="bg-white border border-slate-200 rounded-md text-sm px-3 py-1 text-slate-800 focus:outline-none focus:border-indigo-400 shadow-sm w-1/3"
                                />
                                
                                <select
                                    value={f.operator}
                                    onChange={e => updateFilter(i, 'operator', e.target.value)}
                                    className="bg-white border border-slate-200 rounded-md text-sm px-2 py-1 text-slate-800 outline-none shadow-sm font-mono font-bold"
                                >
                                    <option value="=">=</option>
                                    <option value="!=">!=</option>
                                    <option value=">">&gt;</option>
                                    <option value=">=">&gt;=</option>
                                    <option value="<">&lt;</option>
                                    <option value="<=">&lt;=</option>
                                    <option value="LIKE">LIKE</option>
                                </select>
                                <input
                                    placeholder="'Literal Value'"
                                    value={f.value}
                                    onChange={e => updateFilter(i, 'value', e.target.value)}
                                    className="bg-white border border-slate-200 rounded-md text-sm px-3 py-1 text-emerald-600 focus:outline-none focus:border-indigo-400 shadow-sm w-1/3 font-mono"
                                />
                                <button onClick={() => removeFilter(i)} className="p-1.5 text-red-500 hover:bg-red-100 rounded-md ml-auto transition">
                                    <DynamicIcon name="Trash2" className="w-4 h-4" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
                )}
            </div>

            {/* Status Pane Bottom */}
            <div className="h-1/3 shrink-0 bg-white rounded-xl border border-slate-200 flex flex-col overflow-hidden shadow-sm">
                <div className="flex items-center justify-between px-4 py-2 bg-slate-50 border-b border-slate-200">
                    <span className="text-xs font-semibold text-slate-700">Canvas Live Results</span>
                    <span className="text-xs text-slate-400 font-mono">{rawSql}</span>
                </div>
                <div className="flex-1 overflow-auto p-2">
                    {!previewData ? (
                        <div className="flex items-center justify-center h-full text-xs text-slate-400 italic">Hit "Run Query" to fetch cross-joined results...</div>
                    ) : (
                        <div className="text-xs font-mono text-slate-800 whitespace-pre p-2">
                            <table className="w-full text-left">
                                <thead>
                                    <tr>
                                        {previewData.columns.map(c => (
                                            <th key={c} className="px-3 py-2 border-b border-slate-200 font-semibold">{c}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.rows.map((row, i) => (
                                        <tr key={i} className="hover:bg-slate-50">
                                            {previewData.columns.map(c => (
                                                <td key={c} className="px-3 py-2 border-b border-slate-100 truncate max-w-[200px]">{String(row[c])}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
};

export default VisualQueryBuilder;
